// Copyright (c) 2024 - Present. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted

/**
 * A filter menu component for customer selection with grouping.
 * This module provides a searchable interface for filtering
 * by customers, with support for group selection of all customers
 * and filtering for projects with no customer assigned.
 * It handles complex selection states and search functionality.
 *
 * Key features:
 * - No-customer filtering (projects with null customer)
 * - Customer selection with group checkbox
 * - Search functionality
 * - Group operations (select all/deselect all)
 * - Indeterminate state for partial selection
 * - Real-time updates
 */
import { FilterMenuButton } from '@/components/filter-menu-button';
import { FilterMenuItem } from '@/components/filter-menu-item';
import { SearchRounded } from '@/constants/hardfin-standard-icons';
import { ProjectCompanyType } from '@/constants/project-company-type';
import { useCustomers } from '@/customer/hooks/use-customers';
import { useDisclosure } from '@/hooks/use-disclosure';
import { useTimeout } from '@/hooks/use-timeout';
import {
  Divider,
  InputAdornment,
  InputBase,
  List,
  Popover,
  Stack,
  useTheme,
} from '@mui/material';
import { without } from 'lodash-es';
import { ChangeEventHandler, useRef, useState } from 'react';

/**
 * Props for configuring the CustomerGroupedFilterMenu component.
 *
 * @interface Props
 */
interface Props {
  /**
   * Currently selected company types.
   * Represents high-level project categorization by customer assignment.
   */
  companyTypes: ProjectCompanyType[];

  /**
   * IDs of specific customers that are selected.
   * Used when individual customers are chosen.
   */
  customerIds: string[];

  /**
   * Callback fired when selection changes.
   * Receives updated arrays for both selection types.
   *
   * @param companyTypes - New array of selected company types
   * @param customerIds - New array of selected customer IDs
   */
  onChange: (companyTypes: ProjectCompanyType[], customerIds: string[]) => void;

  /**
   * Optional label for the filter button.
   * Defaults to "Company" if not provided.
   */
  label?: string;

  /**
   * If true, enables the search functionality.
   * Defaults to true.
   */
  enableSearch?: boolean;

  /**
   * If true, includes the "Not set" option as a selectable option.
   * When selected, filters for projects with no customer assigned (customer IS NULL).
   */
  includeNotSet?: boolean;
}

const DEFAULT_LABEL = 'Company';

/**
 * A searchable filter menu for customer selection with grouping.
 * Provides an interface for selecting customers with support for
 * both no-customer filtering and group selection, including search
 * functionality and indeterminate state management.
 *
 * Features:
 * - No-customer selection (filter for projects with no customer)
 * - Customer search
 * - Group selection (select all/deselect all customers)
 * - Indeterminate state for partial selection
 * - Real-time filtering
 * - Selection state management
 *
 * Usage example:
 * ```tsx
 * function ProjectList() {
 *   const [companyTypes, setCompanyTypes] = useState<ProjectCompanyType[]>([]);
 *   const [customerIds, setCustomerIds] = useState<string[]>([]);
 *
 *   const handleChange = (
 *     newCompanyTypes: ProjectCompanyType[],
 *     newCustomerIds: string[]
 *   ) => {
 *     setCompanyTypes(newCompanyTypes);
 *     setCustomerIds(newCustomerIds);
 *   };
 *
 *   return (
 *     <CustomerGroupedFilterMenu
 *       companyTypes={companyTypes}
 *       customerIds={customerIds}
 *       onChange={handleChange}
 *       includeNotSet={true}
 *     />
 *   );
 * }
 * ```
 */
export function CustomerGroupedFilterMenu(props: Props) {
  const theme = useTheme();

  const { customers, isLoading } = useCustomers();

  const menu = useDisclosure();
  const ref = useRef<HTMLButtonElement>(null);
  const [search, setSearch] = useState('');

  const resetSearch = useTimeout(
    () => setSearch(''),
    theme.transitions.duration.standard
  );

  const label = props.label ?? DEFAULT_LABEL;
  const enableSearch = props.enableSearch ?? true;

  const canonicalSearchQuery = search.trim().toLowerCase();
  const isNotSetOptionVisible =
    props.includeNotSet && 'not set'.includes(canonicalSearchQuery);

  const options = customers
    .filter(({ name }) => name.toLowerCase().includes(canonicalSearchQuery))
    .map(({ id, name }) => ({
      id,
      label: name,
      isActive:
        props.companyTypes.includes('customer') ||
        props.customerIds.includes(id),
    }));

  // Use 'customers' to disable the button instead of 'options' because the
  // 'options' array is filtered and may be empty.
  const isDisabled = isLoading || customers.length === 0;

  // The no-customer option (current user's organization) is not included in the
  // options array because it represents projects with no customer. The no-customer
  // option is considered active when the 'no-customer' value is included in the
  // companyTypes prop, so it should be included in the count.
  const initialCount = props.companyTypes.includes('no-customer') ? 1 : 0;
  const count = options.reduce((count, option) => {
    if (option.isActive) {
      return count + 1;
    }

    return count;
  }, initialCount);

  const isCustomerGroupActive =
    props.companyTypes.includes('customer') ||
    customers.every(({ id }) => props.customerIds.includes(id));

  const isCustomerGroupIndeterminate =
    !isCustomerGroupActive &&
    customers.some(({ id }) => props.customerIds.includes(id));

  /**
   * Handles changes to the search input.
   * Updates search state and filters visible options.
   *
   * @param event - The change event from the input
   */
  const handleChangeSearch: ChangeEventHandler<HTMLInputElement> = (event) => {
    setSearch(event.target.value);
  };

  /**
   * Adds a company type to the selection.
   * Handles special cases for customer selection.
   *
   * @param companyType - The company type to enable
   */
  const enableCompanyType = (companyType: ProjectCompanyType) => {
    if (props.companyTypes.includes(companyType)) {
      return;
    }

    if (companyType === 'customer') {
      return props.onChange([...props.companyTypes, companyType], []);
    }

    props.onChange([...props.companyTypes, companyType], props.customerIds);
  };

  /**
   * Handles selection changes for the no-customer option.
   * Updates selection state for no-customer filtering.
   *
   * @param isActive - Whether no-customer is being selected
   */
  const handleChangeNoCustomer = (isActive: boolean) => {
    const companyTypes: ProjectCompanyType[] = isActive
      ? [...props.companyTypes, 'no-customer']
      : without(props.companyTypes, 'no-customer');

    props.onChange(companyTypes, props.customerIds);
  };

  /**
   * Handles selection changes for the customer group.
   * Updates selection state for all customers.
   *
   * @param isActive - Whether customers are being selected
   */
  const handleChangeCustomers = (isActive: boolean) => {
    if (isActive) {
      if (canonicalSearchQuery.length === 0) {
        return enableCompanyType('customer');
      }

      // The 'options' array is already filtered based on the search query, so
      // this array represents the ID of all customers in the current search
      // result set.
      const customerIds = options.map(({ id }) => id);

      return props.onChange(
        without(props.companyTypes, 'customer'),
        customerIds
      );
    }

    props.onChange(without(props.companyTypes, 'customer'), []);
  };

  /**
   * Adds a customer to the selection.
   * Handles group completion if all customers selected.
   *
   * @param customerId - ID of customer to add
   */
  const addCustomerId = (customerId: string) => {
    const customerIds = [...props.customerIds, customerId];

    const hasInactiveCustomers = customers.some(
      (customer) => !customerIds.includes(customer.id)
    );

    if (hasInactiveCustomers) {
      return props.onChange(props.companyTypes, customerIds);
    }

    enableCompanyType('customer');
  };

  /**
   * Removes a customer from the selection.
   * Handles group state updates appropriately.
   *
   * @param customerId - ID of customer to remove
   */
  const removeCustomerId = (customerId: string) => {
    // If the 'customer' value is included in props.companyTypes, then all customers
    // are considered active. In this case, removing a single customer ID should
    // remove the 'customer' value from props.companyTypes and also add all other
    // customer IDs to the ID array.
    if (props.companyTypes.includes('customer')) {
      const customerIds = customers
        .filter((customer) => customer.id !== customerId)
        .map((customer) => customer.id);

      return props.onChange(
        without(props.companyTypes, 'customer'),
        customerIds
      );
    }

    const customerIds = without(props.customerIds, customerId);

    props.onChange(props.companyTypes, customerIds);
  };

  /**
   * Handles menu close events.
   * Resets search state after closing.
   */
  const handleClose = () => {
    menu.close();
    resetSearch.start();
  };

  return (
    <>
      <FilterMenuButton
        ref={ref}
        count={count}
        onClick={menu.open}
        isDisabled={isDisabled}
      >
        {label}
      </FilterMenuButton>
      <Popover
        open={menu.isOpen}
        anchorEl={ref.current}
        onClose={handleClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
        transitionDuration={theme.transitions.duration.standard}
      >
        <Stack paddingTop={1}>
          {enableSearch && (
            <>
              <InputBase
                size="small"
                placeholder="Search companies..."
                onChange={handleChangeSearch}
                sx={{ paddingX: 1, paddingBottom: 0.5 }}
                startAdornment={
                  <InputAdornment position="start">
                    <SearchRounded />
                  </InputAdornment>
                }
              />
              <Divider />
            </>
          )}
          <List sx={{ overflowY: 'auto', maxHeight: '30rem' }}>
            {isNotSetOptionVisible && (
              <>
                <FilterMenuItem
                  isActive={props.companyTypes.includes('no-customer')}
                  onChange={handleChangeNoCustomer}
                >
                  Not set
                </FilterMenuItem>
              </>
            )}
            {isNotSetOptionVisible && options.length > 0 && <Divider />}
            {options.length > 0 && (
              <FilterMenuItem
                isActive={isCustomerGroupActive}
                isIndeterminate={isCustomerGroupIndeterminate}
                onChange={handleChangeCustomers}
              >
                Customers
              </FilterMenuItem>
            )}
            {options.map((option) => {
              const handleChange = (isActive: boolean) => {
                if (isActive) {
                  return addCustomerId(option.id);
                }

                removeCustomerId(option.id);
              };

              return (
                <FilterMenuItem
                  key={option.id}
                  level={1}
                  isActive={option.isActive}
                  onChange={handleChange}
                >
                  {option.label}
                </FilterMenuItem>
              );
            })}
          </List>
        </Stack>
      </Popover>
    </>
  );
}
